import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatCalendar, MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import {
  FECHA_ESTANDAR_FORMATS,
  FechaEstandarAdapter,
} from "../utilerias/fecha-estandar.adapter";
import Swal from "sweetalert2";

import { FirmaFrcComponent } from "../vacaciones/firma-frc/firma-frc.component";
import { FrcFirmadoComponent } from "../vacaciones/frc-firmado/frc-firmado.component";
import { renglonesQueCaben, seDesborda } from "../vacaciones/renglones-que-caben";
import { confirmaAutorizar, pideMotivo } from "../vacaciones/confirma-respuesta";
import { PermisosService } from "./permisos.service";
import { FiltroPermisos, cuentaPermisos, filtraPermisos } from "./filtro-permisos";
import { generaFrc017 } from "./frc017";
import { permisoEnEquipo } from "./permiso-en-equipo";
import {
  GocePermiso,
  Permiso,
  TipoPermiso,
} from "../shared/model/permisos/permiso.model";

/**
 * Permisos del FRC-017: faltar un día, salir antes o llegar tarde.
 *
 * Vive como pestaña dentro de "Vacaciones y Permisos", pero es un módulo
 * aparte: otra tabla, otros endpoints y ninguna relación con el saldo de
 * vacaciones. Comparte con ellas dos cosas a propósito: el permiso de
 * menú (quien puede pedir vacaciones puede pedir permiso) y el modal de
 * firma, que hace exactamente lo mismo en los dos formatos.
 *
 * Aquí se PIDE, y aquí también RESPONDE el líder: la bandeja "esperan tu
 * respuesta" va arriba de los propios, igual que en vacaciones. Estuvo
 * sólo en el calendario de permisos, pero ese menú lo tienen dos
 * personas; los ocho autorizadores de los saldos no podían contestar los
 * permisos de su gente (el servidor sí los dejaba: era la pantalla la
 * que tiraba la lista). El calendario sigue resolviendo también, con el
 * mismo flujo: goce, firma, resolver.
 */
/** Los pasos del asistente de «Pedir permiso». «firma» dura lo que está
 *  abierta la ventana de la firma; «listo», ya enviado. */
type PasoPermiso = "que" | "revisa" | "firma" | "listo";


const MESES_CORTOS = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];
const DIAS_SEMANA = [
  "domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado",
];

@Component({
  selector: "app-permisos",
  standalone: true,
  templateUrl: "./permisos.component.html",
  // Las piezas del asistente, las mismas de la pestaña Vacaciones, y
  // luego lo propio.
  styleUrls: ["../vacaciones/asistente-comun.css", "./permisos.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    FirmaFrcComponent,
    FrcFirmadoComponent,
  ],
  providers: [
    // El calendario en español, con el adaptador de COS: los mismos tres
    // que usa la pestaña de Vacaciones.
    { provide: MAT_DATE_LOCALE, useValue: "es-MX" },
    { provide: DateAdapter, useClass: FechaEstandarAdapter },
    { provide: MAT_DATE_FORMATS, useValue: FECHA_ESTANDAR_FORMATS },
  ],
})
export class PermisosComponent implements OnInit {
  /**
   * Qué parte se enseña (30/09/2026, Norman: «déjalo súper acomodado»: a
   * los líderes la bandeja arriba del asistente les hacía rodar la
   * pantalla):
   *  - «pedir»: el asistente y «Tus permisos» (la pestaña Permisos);
   *  - «resolver»: nada a la vista. Desde el 29/10/2026 «Mi equipo» dibuja
   *    los permisos que esperan con el mismo renglón de las vacaciones
   *    (Norman: «no en una ventana sino que salga como el de vacaciones») y
   *    este componente sólo le presta el flujo de responder: la confirmación
   *    con el goce, la firma del FRC-017, resolver y el FRC-017, como
   *    app-permisos-calendario en «Autorizaciones»;
   *  - «todo»: las dos, como era antes.
   */
  @Input() vista: "todo" | "pedir" | "resolver" = "todo";

  /** Cuántos esperan la respuesta de quien entra, cada vez que se carga:
   *  es la cuenta de la pestaña «Mi equipo». */
  @Output() pendientes = new EventEmitter<number>();

  /** Se resolvió uno: quien presta el flujo («Mi equipo») recarga lo suyo. */
  @Output() resuelto = new EventEmitter<void>();

  cargando = false;
  enviando = false;
  generandoFrc: number | null = null;

  mios: Permiso[] = [];

  /** El año en curso: el de la tarjeta «Tus permisos de …». */
  readonly anio = new Date().getFullYear();

  /**
   * Cuántos lleva este año de cada tipo, para la tarjeta de arriba de «Tus
   * permisos» (el lugar que en Vacaciones tiene el saldo). Cuentan los
   * autorizados y los que esperan respuesta. Se cuentan al cargar.
   */
  cifras: Record<TipoPermiso, number> = { FALTA: 0, SALIDA: 0, ENTRADA: 0 };

  /**
   * Los que esperan MI respuesta: los de mi gente según el saldo de
   * vacaciones, y los de la gente de un líder que hoy no está y a quien
   * suplo por el organigrama (esos vienen con suplenteDe y ausencia).
   */
  porResolver: Permiso[] = [];

  /* ---------------- Filtro y paginación ----------------
     Con los años se juntan permisos y la lista se hace interminable.
     Desde el 30/09/2026 (Norman: «no quiero scroll ... paginado») van por
     páginas de los que caben en la columna derecha junto al asistente
     (ajustaPagina), y el paginado va compacto en el encabezado de la lista
     (anterior, «1 de 3», siguiente) con los botones del estándar
     (table-page__pager-link).

     Y con filtro por estado (Norman: «filtro autorizada, no autorizada y
     pendiente, bien elegante»), del color de la franja de cada renglón y
     con cuántos hay de cada uno; se cuentan al cargar, no en cada ciclo
     de la plantilla. Se prende y se apaga tocándolo; apagado se ven
     todos. Sin un «Todos» aparte, los tres caben en una línea. */

  readonly FILTROS: { id: FiltroPermisos; texto: string; clase: string }[] = [
    { id: "PENDIENTE", texto: "En espera", clase: "asi-filtro--espera" },
    { id: "AUTORIZADO", texto: "Autorizados", clase: "asi-filtro--ok" },
    { id: "RECHAZADO", texto: "No autorizados", clase: "asi-filtro--no" },
  ];

  filtro: FiltroPermisos = "TODOS";
  cuentas: Record<FiltroPermisos, number> = cuentaPermisos([]);
  /** Los míos con el filtro puesto: se arman al cargar y al filtrar. */
  filtrados: Permiso[] = [];

  paginaActual = 1;
  /** Cuántos por página: los que caben (ajustaPagina). */
  tamPagina = 3;

  /** Recién enviado uno, al recargar la lista se va a la página donde
   *  quedó. */
  private irAlNuevo = false;

  @ViewChild("filasPer") private filasPer?: ElementRef<HTMLElement>;

  /** Prende el filtro o, si ya estaba puesto, lo quita. */
  filtra(f: FiltroPermisos): void {
    this.aplicaFiltro(this.filtro === f ? "TODOS" : f);
  }

  private aplicaFiltro(f: FiltroPermisos): void {
    this.filtro = f;
    this.filtrados = filtraPermisos(this.mios, f);
    this.paginaActual = 1;
  }

  /** Cuenta los de cada estado y vuelve a armar la lista filtrada; si el
   *  filtro se quedó sin ninguno (retiró el único en espera), se apaga. */
  private cuenta(): void {
    const c = cuentaPermisos(this.mios);
    this.cuentas = c;
    const f = c[this.filtro] ? this.filtro : "TODOS";
    this.filtro = f;
    this.filtrados = filtraPermisos(this.mios, f);
  }

  /**
   * De cuántos en cuántos: los que caben entre el encabezado de la lista y
   * el pie de la ventana (de 2 a 6). Se estima con el alto de un renglón
   * sencillo y, ya pintados, si los de la página no caben (uno con
   * suplencia u observaciones mide más), uno menos hasta que quepan.
   */
  private ajustaPagina(): void {
    const n = renglonesQueCaben(this.filasPer?.nativeElement, 86, { enColumna: 5 });
    if (n === null) {
      return;
    }
    if (n !== this.tamPagina) {
      this.tamPagina = n;
      this.cdr.detectChanges();
    }
    while (this.tamPagina > 2 && seDesborda(this.filasPer?.nativeElement)) {
      this.tamPagina--;
      this.cdr.detectChanges();
    }
  }

  /** Tras recargar: la página del que se acaba de pedir. */
  private muestraElNuevo(): void {
    if (!this.irAlNuevo) {
      return;
    }
    this.irAlNuevo = false;
    const i = this.filtrados.findIndex((p) => p.idPermiso === this.recienPedido);
    this.paginaActual = i >= 0 ? Math.floor(i / this.tamPagina) + 1 : 1;
    this.cdr.detectChanges();
  }

  @HostListener("window:resize")
  alCambiarVentana(): void {
    this.ajustaPagina();
  }

  /* Lo que espera respuesta ya no se pagina aquí (29/10/2026): en «Mi
     equipo» lo dibuja y lo pagina esa pantalla, junto con las vacaciones,
     y en la vista que junta las dos partes va entero, a lo ancho. */

  miosPagina(): Permiso[] {
    const inicio = (this.paginaNorm() - 1) * this.tamPagina;
    return this.filtrados.slice(inicio, inicio + this.tamPagina);
  }

  totalPaginas(): number {
    return this.filtrados.length > 0 ? Math.ceil(this.filtrados.length / this.tamPagina) : 1;
  }

  /** Acota la página al rango válido: al retirar el último de la
   *  página, la lista se recorre sola en vez de quedar en blanco. */
  paginaNorm(): number {
    return Math.min(Math.max(this.paginaActual, 1), this.totalPaginas());
  }

  irPagina(p: number): void {
    const total = this.totalPaginas();
    if (p < 1 || p > total || p === this.paginaNorm()) {
      return;
    }
    this.paginaActual = p;
    this.cdr.detectChanges();
  }

  paginaAnterior(): void { this.irPagina(this.paginaNorm() - 1); }
  paginaSiguiente(): void { this.irPagina(this.paginaNorm() + 1); }

  /* ---------------- El asistente ----------------
     30/09/2026 (Norman, tras el de Vacaciones: «y permisos sigue igual»):
     pedir va en tres pasos —qué y cuándo, revisa, firma y envía— con el
     calendario a la vista, en vez de un formulario que se desplegaba con
     «Pedir permiso». Las reglas no cambian: lo que antes revisaba `falta`
     al firmar ahora se pide paso por paso (faltaPermiso, faltaFormato). */

  paso: PasoPermiso = "que";

  readonly PASOS: { id: PasoPermiso; texto: string }[] = [
    { id: "que", texto: "Qué y cuándo" },
    { id: "revisa", texto: "Revisa" },
    { id: "firma", texto: "Firma y envía" },
  ];

  /** Lo que se acaba de enviar, para el aviso del paso «listo». */
  enviada: { que: string; papel: boolean } | null = null;

  /** El que se acaba de pedir, para resaltarlo en «Tus permisos» y saber
   *  a quién le llegó. */
  recienPedido: number | null = null;

  /** Si en «Revisa» se ven los campos del encabezado del formato o sólo
   *  sus datos. Se abren solos si falta alguno. */
  editandoFormato = false;

  /* ---------------- El formulario ---------------- */

  /**
   * Las tres opciones del formato, con su etiqueta y qué dato pide cada
   * una. Van en un arreglo y no en tres campos porque en el papel son
   * EXCLUYENTES: se marca una.
   */
  readonly OPCIONES: Array<{
    valor: TipoPermiso;
    texto: string;
    pideHora: boolean;
    etiquetaHora: string;
  }> = [
    {
      valor: "FALTA",
      texto: "Faltar a sus labores el día",
      pideHora: false,
      etiquetaHora: "",
    },
    {
      valor: "SALIDA",
      texto: "Salir dentro de la jornada de trabajo",
      pideHora: true,
      etiquetaHora: "Hora de salida",
    },
    {
      valor: "ENTRADA",
      texto: "Llegar más tarde",
      pideHora: true,
      etiquetaHora: "Hora de entrada",
    },
  ];

  tipo: TipoPermiso = "FALTA";
  /** El día en ISO (yyyy-MM-dd): es lo que viaja al servidor. */
  fecha = "";
  /** El mismo día como Date, para el calendario. */
  fechaDia: Date | null = null;
  hora = "";
  observaciones = "";
  // El goce NO se captura aquí: lo decide quien autoriza, desde el
  // calendario de permisos.

  /**
   * El encabezado del formato. Llega propuesto desde el organigrama y se
   * puede corregir: no toda rama tiene los tres niveles, y el papel se
   * firma con lo que diga aquí.
   */
  area = "";
  directorArea = "";
  gerenteOperativo = "";
  jefeDirecto = "";

  /**
   * Los candidatos a jefe, del organigrama. En un área puede haber
   * varios líderes: se ofrecen todos y quien pide marca el suyo.
   */
  jefes: string[] = [];

  /** El valor que abre el campo de texto, para quien no está en la lista. */
  readonly OTRO = "__OTRO__";
  jefeLibre = false;

  /** Al escoger "Otro…" se cambia a un campo de texto vacío. */
  cambiaJefe(valor: string): void {
    if (valor === this.OTRO) {
      this.jefeDirecto = "";
      this.jefeLibre = true;
    }
  }

  volverALista(): void {
    this.jefeLibre = false;
    // Si lo escrito a mano no está en la lista, el select se quedaría
    // en blanco: se limpia para que muestre el "escoge a tu líder".
    if (this.jefeDirecto && this.jefes.indexOf(this.jefeDirecto) < 0) {
      this.jefeDirecto = "";
    }
  }

  /* ---------------- Firma ---------------- */

  firmaAbierta = false;
  /** El mismo modal firma la solicitud propia o la autorización de otro. */
  firmaPara: "pedir" | "autorizar" = "pedir";
  /** Al autorizar: el permiso que se está firmando, y el goce ya escogido. */
  firmaObjetivo: Permiso | null = null;
  private goceElegido: GocePermiso | null = null;
  resolviendo: number | null = null;

  constructor(
    private service: PermisosService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Prestando el flujo («resolver») no hay nada que cargar: lo que espera
    // respuesta lo trae y lo dibuja «Mi equipo».
    if (this.vista !== "resolver") {
      this.cargar();
    }
  }

  /* ================= Carga ================= */

  cargar(): void {
    this.cargando = true;
    this.service.consultar().subscribe({
      next: (res) => {
        this.cargando = false;
        if (res.codigoError) {
          this.aviso(res.mensajeError || "No se pudieron cargar los permisos.", "error");
          this.cdr.detectChanges();
          return;
        }
        this.mios = res.permisos || [];
        this.cuenta();
        const anio = String(this.anio);
        const cifras: Record<TipoPermiso, number> = { FALTA: 0, SALIDA: 0, ENTRADA: 0 };
        for (const p of this.mios) {
          if (p.tipo && (p.fecha || "").startsWith(anio) &&
              (p.estatus === "PENDIENTE" || p.estatus === "AUTORIZADO")) {
            cifras[p.tipo]++;
          }
        }
        this.cifras = cifras;
        this.porResolver = res.porResolver || [];
        this.jefes = res.jefes || [];
        // Sólo se rellena si el usuario no ha escrito nada: si ya estaba
        // corrigiendo el encabezado, recargar no debe pisárselo.
        const e = res.encabezado;
        if (e && !this.area && !this.directorArea && !this.gerenteOperativo && !this.jefeDirecto) {
          this.area = e.area || "";
          this.directorArea = e.directorArea || "";
          this.gerenteOperativo = e.gerenteOperativo || "";
          // El propuesto se preselecciona sólo si está entre los
          // candidatos; si no, el select mostraría un valor que no
          // puede volver a escoger si lo cambia.
          const propuesto = e.jefeDirecto || "";
          this.jefeDirecto = this.jefes.indexOf(propuesto) >= 0 ? propuesto : "";
        }
        this.pendientes.emit(this.porResolver.length);
        this.cdr.detectChanges();
        // Ya pintada la columna derecha se sabe dónde empieza la lista. Se
        // vuelve a medir cuando llegan las fuentes: sin ellas los íconos
        // son texto y los renglones miden de más.
        this.ajustaPagina();
        this.muestraElNuevo();
        void document.fonts?.ready.then(() => this.ajustaPagina());
      },
      error: () => {
        this.cargando = false;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= Pedir ================= */

  /** La opción escogida, para saber si pide hora y con qué etiqueta. */
  get opcion() {
    return this.OPCIONES.find((o) => o.valor === this.tipo) || this.OPCIONES[0];
  }

  cambiaTipo(t: TipoPermiso): void {
    this.tipo = t;
    // Cambiar a "faltar todo el día" deja sin sentido la hora: se limpia
    // en vez de mandarla y que el backend la ignore en silencio.
    if (!this.opcion.pideHora) {
      this.hora = "";
    }
  }

  /** Lo que falta del permiso mismo (el paso «Qué y cuándo»). Vacío = listo. */
  get faltaPermiso(): string {
    if (!this.fecha) {
      return "Falta el día del permiso.";
    }
    // Red por si acaso: el calendario ya no ofrece esos días.
    if (!this.diaValido(this.fechaDia)) {
      return "El permiso tiene que ser entre semana y de hoy en adelante.";
    }
    if (this.opcion.pideHora && !this.hora) {
      return "Falta " + this.opcion.etiquetaHora.toLowerCase() + ".";
    }
    return "";
  }

  /** Lo que falta del encabezado del formato (el paso «Revisa»). */
  get faltaFormato(): string {
    if (!this.area.trim()) {
      return "Falta el área.";
    }
    if (!this.jefeDirecto.trim() || this.jefeDirecto === this.OTRO) {
      return "Escoge a tu líder de proyecto o jefe directo.";
    }
    return "";
  }

  /** Lo que falta para poder pedir. Vacío = listo. */
  get falta(): string {
    return this.faltaPermiso || this.faltaFormato;
  }

  /* ================= El asistente ================= */

  /** El número del paso en que va (0 a 2), o 3 cuando ya se envió. */
  get indicePaso(): number {
    const i = this.PASOS.findIndex((p) => p.id === this.paso);
    return i < 0 ? this.PASOS.length : i;
  }

  /** A qué paso se puede volver tocándolo arriba: sólo al primero desde
   *  «Revisa» (como en Vacaciones). */
  puedeIrA(i: number): boolean {
    return this.paso === "revisa" && i === 0;
  }

  irAPaso(i: number): void {
    if (this.puedeIrA(i)) {
      this.paso = this.PASOS[i].id;
    }
  }

  /** De «Qué y cuándo» a «Revisa». El encabezado se abre para corregir
   *  sólo si le falta algo. */
  aRevisar(): void {
    if (this.faltaPermiso) {
      return;
    }
    this.editandoFormato = !!this.faltaFormato;
    this.paso = "revisa";
  }

  aQue(): void {
    if (this.paso === "revisa") {
      this.paso = "que";
    }
  }

  /**
   * De «Revisa» a la firma. Se firma ANTES de mandar: el FRC-017 no
   * existe sin la firma de quien lo pide, igual que el FRC-019. Si la
   * cierra sin firmar vuelve a «Revisa» (cancelaFirma).
   */
  firmarYEnviar(): void {
    if (this.paso !== "revisa" || this.falta) {
      return;
    }
    this.paso = "firma";
    this.firmaPara = "pedir";
    this.firmaObjetivo = null;
    this.abreFirma();
  }

  /** «Pedir otro permiso», ya enviado el anterior. */
  otroPermiso(): void {
    this.enviada = null;
    this.recienPedido = null;
    this.paso = "que";
  }

  /** Borra el día, la hora y las observaciones (no el encabezado). */
  limpiaPermiso(): void {
    this.fecha = "";
    this.fechaDia = null;
    this.hora = "";
    this.observaciones = "";
  }

  private enviar(firma: string): void {
    this.enviando = true;
    const permiso: Permiso = {
      tipo: this.tipo,
      fecha: this.fecha,
      hora: this.opcion.pideHora ? this.hora : undefined,
      observaciones: this.observaciones.trim() || undefined,
      area: this.area.trim(),
      directorArea: this.directorArea.trim() || undefined,
      gerenteOperativo: this.gerenteOperativo.trim() || undefined,
      jefeDirecto: this.jefeDirecto.trim() || undefined,
      firmaSolicita: firma,
    };
    this.service.solicitar(permiso).subscribe({
      next: (res) => {
        this.enviando = false;
        if (res.codigoError) {
          // De vuelta a «Revisa», con todo como estaba.
          this.paso = "revisa";
          this.aviso(res.mensajeError || "No se pudo pedir el permiso.", "error");
          this.cdr.detectChanges();
          return;
        }
        const papel = firma === FirmaFrcComponent.FISICA;
        // El aviso de «listo» se arma antes de limpiar el formulario. Sin
        // el «Permiso solicitado» de antes: lo dice el paso «listo», y el
        // permiso aparece resaltado en «Tus permisos».
        this.enviada = { que: this.pedidoEnFrase, papel };
        this.recienPedido = res.idGenerado ?? null;
        // Sin filtro, y al recargar, a la página donde quedó.
        this.aplicaFiltro("TODOS");
        this.irAlNuevo = true;
        this.limpiaForm();
        this.paso = "listo";
        this.cargar();
        // Quien escogió firmar en papel necesita el papel. El modal lo
        // prometió ("Enviar y descargar") y hasta ahora no lo bajaba:
        // había que ir a buscarlo con el botón de la tarjeta.
        if (papel && res.idGenerado != null) {
          this.bajaFrc(res.idGenerado);
        }
      },
      error: () => {
        this.enviando = false;
        this.paso = "revisa";
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  private limpiaForm(): void {
    this.tipo = "FALTA";
    this.fecha = "";
    this.fechaDia = null;
    this.hora = "";
    this.observaciones = "";
    // El encabezado NO se limpia: es el mismo para todos sus permisos y
    // volver a escribirlo cada vez sería trabajo de más.
  }

  /* ================= Retirar ================= */

  retirar(p: Permiso): void {
    if (p.idPermiso == null) {
      return;
    }
    Swal.fire({
      title: "¿Retirar el permiso?",
      text: "Dejará de esperar respuesta. Puedes volver a pedirlo después.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, retirar",
      cancelButtonText: "No",
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.service.cancelar(p.idPermiso!).subscribe({
        next: (res) => {
          if (res.codigoError) {
            this.aviso(res.mensajeError || "No se pudo retirar.", "error");
            this.cdr.detectChanges();
            return;
          }
          this.aviso("Permiso retirado.", "success");
          this.cargar();
        },
        error: () => {
          this.aviso("No se pudo conectar con el servidor.", "error");
          this.cdr.detectChanges();
        },
      });
    });
  }

  /* ================= Archivar =================
     Un retirado se puede archivar para que deje de salir en «Tus
     permisos» (14/10/2026, Norman: «cuando las revoco que me deje
     archivarlas para que no aparezcan ahí»). Sin preguntar: se deshace
     igual de fácil desde «Archivados». */

  /** El que se está archivando o sacando del archivo: dos clics no mandan dos. */
  archivando: number | null = null;

  archivar(p: Permiso, archivar: boolean): void {
    if (p.idPermiso == null || this.archivando !== null) {
      return;
    }
    this.archivando = p.idPermiso;
    this.service.archivar(p.idPermiso, archivar).subscribe({
      next: (res) => {
        this.archivando = null;
        if (res.codigoError) {
          this.aviso(res.mensajeError || "No se pudo archivar.", "error");
          this.cdr.detectChanges();
          return;
        }
        p.archivado = archivar;
        this.cuenta();
        this.aviso(archivar ? "Archivado. Lo ves en «Archivados»." : "De vuelta en tu lista.", "success");
        this.cdr.detectChanges();
      },
      error: () => {
        this.archivando = null;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= Resolver =================
     El mismo flujo que el calendario de permisos —escoger goce, firmar,
     resolver— porque el papel que sale tiene que ser el mismo. Desde el
     29/10/2026 la confirmación y el motivo son los de vacaciones
     (../vacaciones/confirma-respuesta): «¿Autorizar este permiso?» con el
     nombre, el permiso en una frase y el goce a escoger, en vez de «¿Cómo se
     autoriza?» con un botón por goce. */

  autorizar(p: Permiso): void {
    // El goce lo decide quien autoriza, no quien pide: es una concesión
    // de la empresa. Por eso se pregunta aquí y no en la solicitud.
    void confirmaAutorizar({
      titulo: "¿Autorizar este permiso?",
      nombre: p.colaborador,
      detalle: permisoEnEquipo(p, this.anio).frase,
      documento: "FRC-017",
      conGoce: true,
    }).then((r) => {
      if (!r) {
        return;
      }
      this.goceElegido = r.goce || null;
      this.firmaPara = "autorizar";
      this.firmaObjetivo = p;
      this.abreFirma();
    });
  }

  rechazar(p: Permiso): void {
    void pideMotivo(p.colaborador, permisoEnEquipo(p, this.anio).frase, "Ej. Ese día hay entrega y no hay quien cubra.").then(
      (motivo) => {
        if (motivo !== null) {
          this.resuelve(p, false, motivo);
        }
      }
    );
  }

  private resuelve(p: Permiso, aprobar: boolean, motivo?: string, firma?: string): void {
    if (p.idPermiso == null) {
      return;
    }
    this.resolviendo = p.idPermiso;
    // markForCheck y no detectChanges (29/10/2026): en «Mi equipo» los
    // botones son de la pantalla de afuera, que también se tiene que repintar.
    this.cdr.markForCheck();
    const goce = aprobar ? this.goceElegido || undefined : undefined;
    this.service.resolver(p.idPermiso, aprobar, motivo, firma, goce).subscribe({
      next: (res) => {
        this.resolviendo = null;
        if (res.codigoError) {
          this.aviso(res.mensajeError || "No se pudo resolver.", "error");
          this.cdr.markForCheck();
          return;
        }
        this.goceElegido = null;
        this.aviso(aprobar ? "Permiso autorizado." : "Permiso no autorizado.", "success");
        this.resuelto.emit();
        if (this.vista !== "resolver") {
          this.cargar();
        }
        // Quien firma en papel necesita el papel, y es su única
        // oportunidad: el permiso sale de su bandeja al resolverlo.
        if (firma === FirmaFrcComponent.FISICA && p.idPermiso != null) {
          this.bajaFrc(p.idPermiso);
        }
      },
      error: () => {
        this.resolviendo = null;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.markForCheck();
      },
    });
  }

  /* ================= Firma ================= */

  abreFirma(): void {
    this.firmaAbierta = true;
    // Se puede llegar desde el .then de un swal, fuera del ciclo de
    // Angular: sin esto el modal no se pinta.
    this.cdr.detectChanges();
  }

  cierraFirma(): void {
    this.firmaAbierta = false;
    this.firmaObjetivo = null;
    this.cdr.detectChanges();
  }

  /** La cerró sin firmar: si estaba pidiendo, el asistente vuelve a
   *  «Revisa» con todo como estaba. */
  cancelaFirma(): void {
    if (this.firmaPara === "pedir" && this.paso === "firma") {
      this.paso = "revisa";
    }
    this.cierraFirma();
  }

  /** Lo que emite el modal: el PNG trazado, o FISICA para firmar en papel. */
  firmada(firma: string): void {
    const objetivo = this.firmaObjetivo;
    const para = this.firmaPara;
    this.cierraFirma();
    if (para === "autorizar") {
      if (objetivo) {
        this.resuelve(objetivo, true, undefined, firma);
      }
      return;
    }
    this.enviar(firma);
  }

  /* ================= El documento ================= */

  /** Hay FRC-017 de lo pedido con firma y no negado. */
  tieneFrc(p: Permiso): boolean {
    return (
      p.tieneFirma === true &&
      (p.estatus === "PENDIENTE" || p.estatus === "AUTORIZADO")
    );
  }

  frcDe(p: Permiso): void {
    if (p.idPermiso != null) {
      this.bajaFrc(p.idPermiso);
    }
  }

  /**
   * Baja el FRC-017 de un permiso por su id.
   *
   * Aparte de frcDe() porque también se llama recién pedido o recién
   * autorizado, cuando todavía no hay tarjeta en pantalla de la cual
   * sacar el permiso: sólo el id que devolvió el servidor.
   */
  private bajaFrc(idPermiso: number): void {
    this.generandoFrc = idPermiso;
    // Como al resolver: el botón puede ser de «Mi equipo», no de aquí.
    this.cdr.markForCheck();
    this.service.frc(idPermiso).subscribe({
      next: (res) => {
        this.generandoFrc = null;
        const dato = (res.permisos || [])[0];
        if (res.codigoError || !dato) {
          this.aviso(res.mensajeError || "No se pudo armar el FRC-017.", "error");
          this.cdr.markForCheck();
          return;
        }
        generaFrc017(dato);
        this.cdr.markForCheck();
      },
      error: () => {
        this.generandoFrc = null;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.markForCheck();
      },
    });
  }

  /* ================= Presentación ================= */

  iconoDe(tipo?: TipoPermiso): string {
    if (tipo === "FALTA") {
      return "event_busy";
    }
    return tipo === "SALIDA" ? "logout" : "login";
  }

  /** El color del tipo: el mismo del calendario de permisos. */
  tonoDe(tipo?: TipoPermiso): string {
    if (tipo === "FALTA") {
      return "es-falta";
    }
    return tipo === "SALIDA" ? "es-salida" : "es-entrada";
  }

  /**
   * Quién respondió, o a quién espera (el estatus «En espera» ya dice
   * cuál de los dos): va en la línea de abajo del día cuando «Tus
   * permisos» se acomoda como tarjeta en la columna derecha, donde no hay
   * columna «Quién responde» (30/09/2026, Norman: «que se vea mejor»). En
   * la tabla ancha no se enseña.
   */
  quienCorto(p: Permiso): string {
    switch (p.estatus) {
      case "PENDIENTE":
        return p.esperaA || "Sin autorizador asignado";
      case "CANCELADO":
        return "Lo retiraste";
      default:
        return p.autorizaNombre || "";
    }
  }

  /** "Falta todo el día", "Sale a las 15:41": el renglón de abajo del día. */
  detalle(p: Permiso): string {
    if (p.tipo === "FALTA") {
      return "Falta todo el día";
    }
    return (p.tipo === "SALIDA" ? "Sale" : "Llega") + " a las " + (p.hora || "");
  }

  /**
   * "Miércoles 2 de septiembre": el dato principal del renglón. El año
   * sólo si no es el de hoy; con los años la lista junta permisos viejos.
   */
  diaLargo(iso?: string): string {
    if (!iso) {
      return "";
    }
    const p = iso.substring(0, 10).split("-").map(Number);
    // Con año, mes y día sueltos: new Date("yyyy-MM-dd") lo lee en UTC y
    // en México lo recorre al día anterior.
    const d = new Date(p[0], p[1] - 1, p[2]);
    if (Number.isNaN(d.getTime())) {
      return this.bonita(iso);
    }
    const DIAS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
    const anio = p[0] !== new Date().getFullYear() ? " de " + p[0] : "";
    return DIAS[d.getDay()] + " " + this.bonita(iso) + anio;
  }

  /** La franja de color del renglón, por estado. */
  fila(p: Permiso): string {
    switch (p.estatus) {
      case "PENDIENTE":
        return "es-espera";
      case "AUTORIZADO":
        return "es-ok";
      case "RECHAZADO":
        return "es-no";
      default:
        return "es-retirado";
    }
  }

  iconoEstatus(p: Permiso): string {
    switch (p.estatus) {
      case "PENDIENTE":
        return "hourglass_top";
      case "AUTORIZADO":
        return "check_circle";
      case "RECHAZADO":
        return "cancel";
      default:
        return "undo";
    }
  }

  etiqueta(p: Permiso): string {
    switch (p.estatus) {
      case "PENDIENTE":
        // Corto: a quién se espera, y por qué lo ve otro si el de siempre
        // está fuera, van en «Quién responde» (25/09/2026). En el badge
        // la frase entera lo estiraba a tres renglones.
        return "En espera";
      case "AUTORIZADO":
        return "Autorizado";
      case "RECHAZADO":
        return "No autorizado";
      case "CANCELADO":
        return "Retirado";
      default:
        return p.estatus || "";
    }
  }

  /** Modificadores del badge estandar (table-page__status-badge). */
  clase(p: Permiso): string {
    switch (p.estatus) {
      case "PENDIENTE":
        return "is-pending";
      case "AUTORIZADO":
        return "is-active";
      case "RECHAZADO":
        return "is-danger";
      default:
        return "is-inactive";
    }
  }

  /** "15 de septiembre" — para leerlo, no para capturarlo. */
  bonita(iso?: string): string {
    if (!iso) {
      return "";
    }
    const MESES = [
      "enero", "febrero", "marzo", "abril", "mayo", "junio",
      "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
    ];
    const p = iso.substring(0, 10).split("-");
    return Number(p[2]) + " de " + (MESES[Number(p[1]) - 1] || "");
  }

  trackPermiso = (_: number, p: Permiso) => p.idPermiso;

  /* ================= Las frases del asistente ================= */

  /** «Faltar todo el día», «Salir a las 14:30», «Llegar a las 10:00»:
   *  la línea grande de «Revisa». */
  get queTexto(): string {
    if (this.tipo === "FALTA") {
      return "Faltar todo el día";
    }
    return (this.tipo === "SALIDA" ? "Salir" : "Llegar") + " a las " + this.hora;
  }

  /** «Faltas el viernes 2 de octubre»: la frase del primer paso en cuanto
   *  está completo. */
  get sintesisTitulo(): string {
    const verbo = this.tipo === "FALTA" ? "Faltas" : this.tipo === "SALIDA" ? "Sales" : "Llegas";
    return `${verbo} el ${this.diaMinusculas(this.fecha)}`;
  }

  get sintesisDetalle(): string {
    return this.tipo === "FALTA" ? "Todo el día" : `A las ${this.hora}`;
  }

  /** «faltar el viernes 2 de octubre», «salir el viernes 2 de octubre a
   *  las 14:30»: lo que dice «¡Listo!» tras «Pediste». */
  get pedidoEnFrase(): string {
    const dia = this.diaMinusculas(this.fecha);
    if (this.tipo === "FALTA") {
      return `faltar el ${dia}`;
    }
    return `${this.tipo === "SALIDA" ? "salir" : "llegar"} el ${dia} a las ${this.hora}`;
  }

  /** Lo pedido en una frase, para el lector de pantalla de «Revisa». */
  get enUnaFrase(): string {
    return `${this.queTexto}, ${this.diaMinusculas(this.fecha)}`;
  }

  /** A quién le llegó el que se acaba de pedir, según la lista recién
   *  cargada: el servidor decide quién lo resuelve, no esta pantalla. */
  get quienResponde(): string {
    const p = this.mios.find((x) => x.idPermiso === this.recienPedido);
    return (p && p.esperaA) || "";
  }

  /** «falta» o «faltas», «llegada tarde» o «llegadas tarde». */
  etiquetaCifra(t: TipoPermiso): string {
    const n = this.cifras[t];
    const nombre = t === "FALTA" ? "falta" : t === "SALIDA" ? "salida" : "llegada";
    const plural = n === 1 ? nombre : nombre + "s";
    return t === "ENTRADA" ? plural + " tarde" : plural;
  }

  /** «vie 2 oct», y «vie 1 ene 2027» si no es de este año: la casilla del
   *  día en el primer paso. */
  diaCorto(d: Date | null): string {
    if (!d) {
      return "";
    }
    const anio = d.getFullYear() !== this.anio ? ` ${d.getFullYear()}` : "";
    return `${DIAS_SEMANA[d.getDay()].substring(0, 3)} ${d.getDate()} ${MESES_CORTOS[d.getMonth()]}${anio}`;
  }

  /** La hojita de calendario de «Revisa»: mes, día de la semana y el año
   *  sólo si no es este. */
  mesDe(d: Date | null): string {
    return d ? MESES_CORTOS[d.getMonth()] : "";
  }

  semanaDe(d: Date | null): string {
    return d ? DIAS_SEMANA[d.getDay()] : "";
  }

  anioSiOtro(d: Date | null): string {
    return d && d.getFullYear() !== this.anio ? `${d.getFullYear()}` : "";
  }

  /** «viernes 2 de octubre» para ir dentro de una frase. */
  private diaMinusculas(iso?: string): string {
    return this.diaLargo(iso).toLowerCase();
  }

  /* ================= El día del permiso ================= */

  /**
   * Hoy, a las cero horas: el mínimo del calendario. Va en una propiedad
   * y no calculado en la plantilla, porque un Date nuevo en cada ciclo de
   * detección de cambios le cambiaría la entrada al calendario sin parar.
   */
  readonly hoy = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

  /**
   * Lo que el calendario deja escoger: de lunes a viernes y de hoy en
   * adelante. Desde el 14/09/2026 no se piden permisos para sábado ni
   * domingo (no se trabaja) ni para días que ya pasaron. El servidor
   * aplica la misma regla (PermisoBuss.validaDiaPermiso).
   *
   * Flecha y no método: el [dateFilter] del calendario guarda la
   * referencia y un método normal perdería el this.
   */
  soloDiasValidos = (d: Date | null): boolean => this.diaValido(d);

  private diaValido(d: Date | null): boolean {
    if (!d) {
      return false;
    }
    const dia = d.getDay();
    if (dia === 0 || dia === 6) {
      return false;
    }
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()) >= this.hoy;
  }

  /** El fin de semana en gris, igual que en Vacaciones. */
  claseDia = (d: Date): string => {
    const dia = d ? d.getDay() : -1;
    return dia === 0 || dia === 6 ? "es-finde" : "";
  };

  alElegirDia(d: Date | null): void {
    this.fechaDia = d;
    this.fecha = d ? this.iso(d) : "";
    this.cdr.detectChanges();
  }

  /** El calendario del primer paso, mientras se ve. */
  @ViewChild(MatCalendar) private calendario?: MatCalendar<Date>;

  /** «Hoy» de la leyenda: el calendario vuelve al mes de hoy, sin elegir
   *  nada (30/09/2026, Norman: «si le doy click que me regrese a hoy»). */
  verMes(fecha: Date): void {
    if (this.calendario) {
      this.calendario.activeDate = fecha;
    }
  }

  private iso(d: Date): string {
    const mes = `${d.getMonth() + 1}`.padStart(2, "0");
    const dia = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${mes}-${dia}`;
  }

  private aviso(texto: string, icono: "success" | "error" | "warning"): void {
    Swal.fire({
      text: texto,
      icon: icono,
      timer: icono === "success" ? 1800 : undefined,
      showConfirmButton: icono !== "success",
    });
  }
}
